import { SVGProps } from 'react';

/* ============================================================
   Module icons (Angle / Height / Torsion)
   ------------------------------------------------------------
   Replaces Unicode text icons (∠ ↔ ⟳) which rendered as missing-
   glyph "?" in WKWebView because SF Pro doesn't carry every
   Mathematical-Symbol / Supplemental-Arrows codepoint. Inline
   SVG side-steps font fallback entirely.

   API: each component accepts standard SVG props (size via
   width/height, color via `color="…"` on a wrapper, since stroke
   is `currentColor`).
   ============================================================ */

type IconProps = SVGProps<SVGSVGElement>;

const STROKE_WIDTH = 2;

/** Two-line goniometer angle with an indicating arc. */
export function AngleIcon(props: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={STROKE_WIDTH}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      {/* horizontal baseline */}
      <path d="M3.5 19.5 L20.5 19.5" />
      {/* angled line meeting baseline at the lower-left vertex */}
      <path d="M3.5 19.5 L17 5" />
      {/* arc indicating the angle */}
      <path d="M9 19.5 A 5.5 5.5 0 0 0 12.2 15.4" />
    </svg>
  );
}

/** Horizontal distance with caliper-style end marks and arrow heads. */
export function HeightIcon(props: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={STROKE_WIDTH}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      {/* left and right caliper marks */}
      <path d="M4 6 L4 18" />
      <path d="M20 6 L20 18" />
      {/* horizontal measurement line */}
      <path d="M6.5 12 L17.5 12" />
      {/* arrow heads */}
      <path d="M9 9 L6 12 L9 15" />
      <path d="M15 9 L18 12 L15 15" />
    </svg>
  );
}

/** Circular torsion / rotation arrow (≈ 3/4 turn + chevron tip). */
export function TorsionIcon(props: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={STROKE_WIDTH}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      {...props}
    >
      {/* ~270° arc, starting top-right, sweeping clockwise to top */}
      <path d="M19 6.5 A 8 8 0 1 1 12 4" />
      {/* arrow head at the start of the arc */}
      <path d="M19 11 L19 6.5 L14.5 6.5" />
    </svg>
  );
}
